import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import _ from "lodash";
import JWT from "jsonwebtoken";
import moment from "moment-timezone";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";

import { LOGIN_USER } from "../../actions/types";
import { LOGGED_IN_EMAIL, USER_OU, PERMISSIONS, CAPTIVE_ACCESS_ENABLED, MAINTENANCE } from "../../constants/Constants";
import { setDataToLocalStorage } from "../../utils/util";
import { getUserPolicies, getResourceList, getMaintenanceInfo } from "../../actions/Users/authenticateService";
import { updateUserOu, updateSelectedOu, fetchDpWiseSideMenu, fetchServiceLines } from "../../actions/Users/authenticate";
import { performPostLoginRedirect } from "../../utils/postLoginRedirect";

function SSOCallback() {
  const dispatch = useDispatch();
  const history = useHistory();
  const authReducer = useSelector((state) => state.authReducer);
  const [errorMsg, setErrorMsg] = useState("");
  const [payloadReady, setPayloadReady] = useState(false);

  const failAndRedirect = (message) => {
    setErrorMsg(message);
    toast.error(message);
    setTimeout(() => history.replace("/login"), 1500);
  };

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    if (errorParam) {
      if (errorParam === "account_disabled") {
        failAndRedirect("Your account has been deactivated. Contact your administrator.");
      } else if (errorParam === "access_denied") {
        failAndRedirect("Access denied. Contact your administrator.");
      } else {
        failAndRedirect(`SSO login failed: ${errorParam}`);
      }
      return;
    }

    const match = hash.match(/^#p=(.+)$/);
    if (!match) {
      failAndRedirect("Missing SSO payload");
      return;
    }

    let payload;
    try {
      const base64 = match[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64 + "===".slice((base64.length + 3) % 4);
      const json = atob(padded);
      payload = JSON.parse(json);
    } catch (e) {
      failAndRedirect("Invalid SSO payload");
      return;
    }

    const loggedInUser = { data: payload };
    setDataToLocalStorage("loggedInUser", JSON.stringify(loggedInUser));
    if (payload.auth_type) {
      setDataToLocalStorage("authType", payload.auth_type);
    }
    if (payload.user && payload.user.userName) {
      setDataToLocalStorage(LOGGED_IN_EMAIL, payload.user.userName);
    } else {
      try {
        const jwtUser = JWT.decode(payload.id_token);
        if (jwtUser && jwtUser.sub) {
          setDataToLocalStorage(LOGGED_IN_EMAIL, jwtUser.sub);
        }
      } catch (e) {
      }
    }
    if (payload.ou) {
      setDataToLocalStorage(USER_OU, JSON.stringify(payload.ou));
    }
    if (typeof payload.user?.ccfEnabled === "boolean") {
      localStorage.setItem("ccfEnabled", String(payload.user.ccfEnabled));
    }
    if (typeof payload.user?.pacHubEnabled === "boolean") {
      localStorage.setItem("pacHubEnabled", String(payload.user.pacHubEnabled));
    }

    dispatch({ type: LOGIN_USER, payload: loggedInUser });
    if (payload.ou) {
      dispatch(updateUserOu(payload.ou));
      dispatch(updateSelectedOu(payload.ou));
    }
    dispatch(getUserPolicies());
    if (payload.ou?.id) {
      dispatch(fetchDpWiseSideMenu(payload.ou.id));
      dispatch(getMaintenanceInfo(payload.ou.id));
    }

    window.history.replaceState(null, "", "/sso-callback");
    setPayloadReady(true);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(authReducer.getMaintenanceInfo)) {
      const data = authReducer.getMaintenanceInfo?.data;
      if (data?.rows && Array.isArray(data.rows) && data.rows.length === 1) {
        const rows = data.rows[0];
        const maintenanceDate = rows[1] || "";
        const maintenanceFromTime = rows[2] || "";
        const maintenanceToTime = rows[3] || "";
        const isBanner = rows[6] || "";
        const maintenanceMessageAvail = rows[7] || "";
        if (maintenanceDate && maintenanceFromTime && maintenanceToTime) {
          const d = moment(maintenanceDate, "YYYY-MM-DD");
          const message = `On ${d.format("ddd")} ${d.format("Do")} ${d.format("MMM")} from ${maintenanceFromTime} to ${maintenanceToTime} UTC`;
          localStorage.setItem(MAINTENANCE, message);
        }
        if (maintenanceMessageAvail && isBanner === "true") {
          localStorage.setItem("ISBANNER", true);
          localStorage.setItem(MAINTENANCE, maintenanceMessageAvail);
        } else {
          localStorage.setItem("ISBANNER", false);
        }
      }
      authReducer.getMaintenanceInfo = {};
    }
  }, [authReducer.getMaintenanceInfo]);

  useEffect(() => {
    if (!payloadReady) {
      return;
    }
    if (!_.isEmpty(authReducer.userPolicy) && authReducer.dpWiseSideMenu !== undefined) {
      const retPolicy = authReducer.userPolicy;
      if (retPolicy.permissions && retPolicy.permissions.length > 0) {
        localStorage.setItem(PERMISSIONS, JSON.stringify(retPolicy.permissions));

        const captiveAccessEnabled = authReducer.loginUser?.data?.ou?.captiveAccess;
        const permCheck = retPolicy.permissions;
        const viewCaptive = permCheck?.find(item => item?.service === "serviceportal")
          ?.servicefunctions?.find(item => item?.function === "ViewCaptivePortal");
        if (captiveAccessEnabled && viewCaptive) {
          localStorage.setItem(CAPTIVE_ACCESS_ENABLED, "true");
        }
      }

      const redirect = () => performPostLoginRedirect({ dispatch, history, authReducer });
      if (authReducer?.userPolicy?.root_ou && !_.isEmpty(authReducer?.userPolicy?.root_ou)) {
        dispatch(getResourceList(authReducer.userPolicy.root_ou, "", "serviceportal", "ViewVesselSummary", redirect));
        dispatch(fetchServiceLines(authReducer?.loginUser?.data?.ou?.id));
      } else {
        performPostLoginRedirect({ dispatch, history, authReducer });
      }
    }
  }, [payloadReady, authReducer.userPolicy, authReducer.dpWiseSideMenu]);

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: "100vh" }}>
      {errorMsg ? (
        <div style={{ textAlign: "center" }}>
          <p>{errorMsg}</p>
          <p>Redirecting to login...</p>
        </div>
      ) : (
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
          <p>Finalizing sign in...</p>
        </div>
      )}
    </Grid>
  );
}

export default SSOCallback;
